import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';

import { StyledForm } from '../../../Hoc/elements';
import Button from '../../../Components/UI/Forms/Button/Button';
import Input from '../../../Components/UI/Forms/Inputs/Input';
import Message from '../../../Components/UI/Messages/Message';
import AddressLocationPicker from '../../../Components/Geo/AddressLocationPicker';
import Modal from '../../../Components/UI/Modal/Modal';
import ProfilePhotoEditor from '../../../Components/Profile/ProfilePhotoEditor';
import ProfileSecurityCard from '../../../Components/Profile/ProfileSecurityCard';
import ReputationSection from '../../../Components/Trust/ReputationSection';
import ProfileLanguageSettings from '../../../Components/I18n/ProfileLanguageSettings';
import { useI18n } from '../../../i18n/I18nProvider';
import * as actions from '../../../Store/Actions';
import {
  ActionBar,
  DangerCard,
  FieldGrid,
  FormStack,
  Full,
  ProfileGrid,
  ProfileHeader,
  ProfilePage,
  SectionCard,
  SectionHeader,
} from '../../../Components/Profile/ProfilePageShell';
import {
  DeleteButton,
  ModalActions,
  ModalContent,
  SaveState,
} from './ProfileFormLayout';

const phoneRegExp = /^(?:(?:\+|00)33|0)[1-9](?:[ .-]?\d{2}){4}$/;

const buildProfileSchema = (pick) => Yup.object({
  firstName: Yup.string().trim()
    .required(pick('Votre prénom est requis.', 'Your first name is required.'))
    .min(2, pick('Trop court.', 'Too short.'))
    .max(40, pick('Trop long.', 'Too long.')),
  lastName: Yup.string().trim()
    .required(pick('Votre nom est requis.', 'Your last name is required.'))
    .min(2, pick('Trop court.', 'Too short.'))
    .max(50, pick('Trop long.', 'Too long.')),
  email: Yup.string().trim()
    .email(pick('Adresse e-mail invalide.', 'Invalid email address.'))
    .required(pick('L’adresse e-mail est requise.', 'Email address is required.')),
  phoneNumber: Yup.string().trim().test(
    'phone-format',
    pick('Utilisez un numéro français valide.', 'Use a valid French phone number.'),
    (value) => !String(value || '').trim() || phoneRegExp.test(String(value).trim()),
  ),
  address: Yup.string().trim().max(180, pick('Adresse trop longue.', 'Address is too long.')),
});

const Profile = ({ firebase, editProfile, loading, error, loadingDelete, errorDelete, deleteUser, cleanUp }) => {
  const { locale, pick } = useI18n();
  const profileSchema = useMemo(() => buildProfileSchema(pick), [pick]);
  const [modalOpened, setModalOpened] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');

  useEffect(() => () => cleanUp(), [cleanUp]);

  if (!firebase.profile.isLoaded) return null;

  return (
    <>
      <Formik
        enableReinitialize
        validateOnMount
        initialValues={{
          firstName: firebase.profile.firstName || '',
          lastName: firebase.profile.lastName || '',
          email: firebase.auth.email || '',
          phoneNumber: String(firebase.profile.phone_number || ''),
          address: firebase.profile.address || firebase.profile.home_location?.formatted_address || '',
          addressLocation: firebase.profile.home_location ? {
            latitude: firebase.profile.home_location.latitude,
            longitude: firebase.profile.home_location.longitude,
            formattedAddress: firebase.profile.home_location.formatted_address || firebase.profile.address || '',
            city: firebase.profile.home_location.city || '',
            postalCode: firebase.profile.home_location.postal_code || '',
            geohash: firebase.profile.home_location.geohash || '',
          } : null,
          profileImage: firebase.profile.profile_image || '',
          userType: firebase.profile.user_type || 'Client',
        }}
        validationSchema={profileSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const updated = await editProfile(values);
          if (updated) {
            resetForm({ values });
            setSavedAt(new Date());
          }
          setSubmitting(false);
        }}
      >
        {({ dirty, isSubmitting, isValid, values, setFieldValue }) => (
          <ProfilePage>
            <ProfileHeader>
              <div>
                <h1>{pick('Mon profil', 'My profile')}</h1>
              </div>
            </ProfileHeader>

            <StyledForm>
              <ProfileGrid>
                <ProfilePhotoEditor
                  image={values.profileImage}
                  name={[values.firstName, values.lastName].filter(Boolean).join(' ')}
                  role="Client"
                  onUploaded={(url) => setFieldValue('profileImage', url)}
                />

                <FormStack>
                  <SectionCard>
                    <SectionHeader>
                      <div>
                        <h2>{pick('Identité et contact', 'Identity and contact')}</h2>
                      </div>
                    </SectionHeader>
                    <FieldGrid>
                      <Field name="firstName" label={pick('Prénom', 'First name')} autoComplete="given-name" component={Input} />
                      <Field name="lastName" label={pick('Nom', 'Last name')} autoComplete="family-name" component={Input} />
                      <Full><Field type="tel" name="phoneNumber" label={pick('Téléphone', 'Phone')} autoComplete="tel" inputMode="tel" component={Input} /></Full>
                    </FieldGrid>
                  </SectionCard>

                  <SectionCard>
                    <SectionHeader>
                      <div>
                        <h2>{pick('Adresse et localisation', 'Address and location')}</h2>
                      </div>
                    </SectionHeader>
                    <FieldGrid>
                      <Full>
                        <AddressLocationPicker
                          id="client-address"
                          label={pick('Adresse principale', 'Main address')}
                          value={values.address}
                          location={values.addressLocation}
                          onAddressChange={(next) => setFieldValue('address', next, true)}
                          onLocationChange={(next) => setFieldValue('addressLocation', next, true)}
                          placeholder={pick('Votre adresse postale', 'Your postal address')}
                          showMap
                        />
                      </Full>
                    </FieldGrid>
                  </SectionCard>

                  <SectionCard>
                    <ReputationSection profile={firebase.profile} userId={firebase.auth.uid} role="Client" />
                  </SectionCard>

                  <SectionCard>
                    <ProfileSecurityCard email={values.email} />
                  </SectionCard>

                  <ProfileLanguageSettings />

                  <Message error show={Boolean(error)}>{error}</Message>

                  <ActionBar $visible={dirty || isSubmitting || loading}>
                    <SaveState $dirty={dirty} $saving={isSubmitting || loading}>
                      <strong>{isSubmitting || loading ? pick('Enregistrement en cours…', 'Saving…') : dirty ? pick('Modifications non enregistrées', 'Unsaved changes') : savedAt ? pick('Profil enregistré', 'Profile saved') : pick('Profil à jour', 'Profile up to date')}</strong>
                      <span>{savedAt && !dirty ? pick(`Dernière sauvegarde à ${savedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`, `Last saved at ${savedAt.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`) : dirty ? pick('Vos changements seront visibles après la sauvegarde.', 'Your changes will be visible after saving.') : pick('Aucune action nécessaire.', 'No action needed.')}</span>
                    </SaveState>
                    <Button contain type="submit" disabled={!dirty || !isValid || isSubmitting || loading} loading={isSubmitting || loading ? pick('Mise à jour…', 'Updating…') : null}>
                      {pick('Enregistrer les modifications', 'Save changes')}
                    </Button>
                  </ActionBar>

                  <DangerCard>
                    <h2>{pick('Zone sensible', 'Sensitive area')}</h2>
                    <p>{pick('La suppression du compte est définitive et efface votre accès à l’application.', 'Deleting your account is permanent and removes your access to the application.')}</p>
                    <DeleteButton type="button" onClick={() => setModalOpened(true)}>{pick('Supprimer mon compte', 'Delete my account')}</DeleteButton>
                  </DangerCard>
                </FormStack>
              </ProfileGrid>
            </StyledForm>
          </ProfilePage>
        )}
      </Formik>

      <Modal opened={modalOpened} close={() => { setModalOpened(false); setDeletePassword(''); }}>
        <ModalContent>
          <h2>{pick('Supprimer mon compte', 'Delete my account')}</h2>
          <p>{pick('Cette action est irréversible. Confirmez-la avec votre mot de passe LPB. Les comptes Google utilisent une confirmation Google sécurisée.', 'This action cannot be undone. Confirm it with your LPB password. Google accounts use secure Google confirmation.')}</p>
          {(firebase.auth.providerData || []).some((provider) => provider?.providerId === 'password') && (
            <label style={{ display: 'grid', gap: '.45rem', marginBottom: '1rem', fontWeight: 800 }}>
              {pick('Mot de passe', 'Password')}
              <input
                type="password"
                value={deletePassword}
                onChange={(event) => setDeletePassword(event.target.value)}
                autoComplete="current-password"
                placeholder={pick('Votre mot de passe', 'Your password')}
                style={{ minHeight: '3rem', border: '1px solid #d8d1c8', borderRadius: '.8rem', padding: '.75rem .9rem', font: 'inherit' }}
              />
            </label>
          )}
          <ModalActions>
            <Button contain type="button" onClick={() => setModalOpened(false)}>{pick('Annuler', 'Cancel')}</Button>
            <Button contain type="button" color="red" onClick={async () => {
                const deleted = await deleteUser(deletePassword);
                if (deleted) {
                  setDeletePassword('');
                  setModalOpened(false);
                }
              }} disabled={loadingDelete} loading={loadingDelete ? pick('Suppression…', 'Deleting…') : null}>
              {pick('Supprimer le compte', 'Delete account')}
            </Button>
          </ModalActions>
          <Message error show={Boolean(errorDelete)}>{errorDelete}</Message>
        </ModalContent>
      </Modal>
    </>
  );
};

const mapStateToProps = ({ firebase, auth }) => ({
  firebase,
  loading: auth.profileEdit.loading,
  error: auth.profileEdit.error,
  loadingDelete: auth.deleteUser.loading,
  errorDelete: auth.deleteUser.error,
});

export default connect(mapStateToProps, {
  editProfile: actions.editProfile,
  cleanUp: actions.cleanMessage,
  deleteUser: actions.deleteUser,
})(Profile);
